import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Pause, Play, XCircle, RotateCcw, Ban, History } from "lucide-react";

const EDIT_REASONS = [
  ["wrong_employee", "Wrong employee assigned"], ["wrong_quantity", "Wrong quantity"],
  ["wrong_deadline", "Wrong deadline"], ["customer_revised_quantity", "Customer revised quantity"],
  ["production_schedule_changed", "Production schedule changed"], ["warehouse_requirement_changed", "Warehouse requirement changed"],
  ["data_entry_correction", "Data entry correction"], ["other", "Other"],
];

export function TaskAdminRow({ task, isAdmin, isManager, canManage, onAction }) {
  if (!canManage) return null;
  const s = task.status;
  return (
    <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/50" data-testid={`task-admin-${task.task_id}`}>
      {isManager && !["completed", "cancelled", "voided"].includes(s) && (
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAction("edit", task)} data-testid={`edit-task-${task.task_id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>)}
      {["assigned", "in_progress", "paused"].includes(s) && (
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAction("hold", task)} data-testid={`hold-task-${task.task_id}`}><Pause className="w-3 h-3 mr-1" /> Hold</Button>)}
      {s === "on_hold" && (
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAction("unhold", task)} data-testid={`unhold-task-${task.task_id}`}><Play className="w-3 h-3 mr-1" /> Unhold</Button>)}
      {isManager && s === "completed" && (
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAction("reopen", task)} data-testid={`reopen-task-${task.task_id}`}><RotateCcw className="w-3 h-3 mr-1" /> Reopen</Button>)}
      {isManager && !["completed", "cancelled", "voided"].includes(s) && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-signal-red" onClick={() => onAction("cancel", task)} data-testid={`cancel-task-${task.task_id}`}><XCircle className="w-3 h-3 mr-1" /> Cancel</Button>)}
      {isAdmin && ["assigned", "on_hold", "cancelled"].includes(s) && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-signal-red" onClick={() => onAction("void", task)} data-testid={`void-task-${task.task_id}`}><Ban className="w-3 h-3 mr-1" /> Void</Button>)}
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAction("history", task)} data-testid={`history-task-${task.task_id}`}><History className="w-3 h-3 mr-1" /> History</Button>
    </div>
  );
}

export function TaskReasonDialog({ action, task, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  useEffect(() => setReason(""), [task?.task_id, action]);
  if (!task) return null;
  return (
    <Dialog open={!!task} onOpenChange={() => onClose()}>
      <DialogContent className="bg-card border-border" data-testid="task-reason-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{action} — {task.task_id}</DialogTitle>
          <DialogDescription className="sr-only">Capture the audited reason for this task action</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Reason *</Label>
            <Textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} data-testid="task-reason-input" />
          </div>
          <Button className="w-full bg-primary text-primary-foreground" disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())} data-testid="task-reason-submit">
            CONFIRM {String(action || "").toUpperCase()}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function TaskEditDialog({ task, onClose, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  if (!task) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.reason) return toast.error("Reason for change is required");
    setBusy(true);
    try {
      const payload = { reason: f.reason, reason_note: f.reason_note || null };
      if (f.quantity) payload.quantity = parseInt(f.quantity);
      if (f.deadline_minutes) payload.deadline_minutes = parseInt(f.deadline_minutes);
      if (f.priority) payload.priority = f.priority;
      if (f.location) payload.location = f.location;
      if (f.remarks) payload.remarks = f.remarks;
      const r = await api.patch(`/tasks/${task.task_id}/edit`, payload);
      if (r.data.warning) toast.warning(r.data.warning);
      toast.success("Task updated (audited)");
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Edit failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!task} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border" data-testid="task-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Edit Task — {task.task_id}</DialogTitle><DialogDescription className="sr-only">Edit task fields with an audited reason</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Quantity ({task.quantity ?? "—"})</Label><Input type="number" placeholder={String(task.quantity ?? "")} value={f.quantity || ""} onChange={(e) => set("quantity", e.target.value)} data-testid="te-quantity" /></div>
            <div><Label className="text-xs">New deadline (minutes from now)</Label><Input type="number" value={f.deadline_minutes || ""} onChange={(e) => set("deadline_minutes", e.target.value)} data-testid="te-deadline" /></div>
            <div><Label className="text-xs">Priority</Label>
              <Select value={f.priority || task.priority || "medium"} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="te-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Location</Label><Input placeholder={task.location || "—"} value={f.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="te-location" /></div>
          </div>
          <div><Label className="text-xs">Reason for change *</Label>
            <Select value={f.reason || ""} onValueChange={(v) => set("reason", v)}>
              <SelectTrigger data-testid="te-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>{EDIT_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          {f.reason === "other" && (
            <div><Label className="text-xs">Remarks *</Label><Textarea value={f.reason_note || ""} onChange={(e) => set("reason_note", e.target.value)} data-testid="te-reason-note" /></div>)}
          <Button className="w-full bg-primary text-primary-foreground" disabled={busy} onClick={submit} data-testid="te-save">SAVE (audited)</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TaskHistoryDialog({ task, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!task?.task_id) {
      setData(null);
      return undefined;
    }
    let active = true;
    setData(null);
    api.get(`/tasks/${task.task_id}/history`)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => toast.error("Failed to load task history"));
    return () => { active = false; };
  }, [task?.task_id]);
  if (!task) return null;
  return (
    <Dialog open={!!task} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto" data-testid="task-history-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">History — {task.task_id}</DialogTitle><DialogDescription className="sr-only">Review task field edits and lifecycle audit events</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Field edits</p>
            {(data?.edits || []).map((e) => (
              <p key={`${e.at}-${e.field}`} className="text-xs border-b border-border/40 py-1">
                <span className="metric text-primary">{e.field}</span>: {String(e.old ?? "—")} → {String(e.new ?? "—")}
                <span className="text-muted-foreground"> · {e.changed_by} · {fmtDT(e.at)} · reason: {e.reason}{e.reason_note ? ` (${e.reason_note})` : ""}</span>
              </p>
            ))}
            {!(data?.edits || []).length && <p className="text-xs text-muted-foreground">No edits</p>}
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Lifecycle / audit</p>
            {(data?.audit || []).map((a) => (
              <p key={`${a.at}-${a.action}`} className="text-xs border-b border-border/40 py-1">{a.action} <span className="text-muted-foreground">· {a.user_name} · {fmtDT(a.at)}</span></p>
            ))}
          </div>
          {task.original_quantity != null && <p className="text-xs text-signal-amber">Original quantity: {task.original_quantity}</p>}
          {task.original_deadline && <p className="text-xs text-signal-amber">Original deadline: {fmtDT(task.original_deadline)}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
